import { useState, useEffect, useCallback } from 'react';

export type ThemeName = 'default' | 'blue' | 'green' | 'red' | 'rose' | 'orange';
export type TweakcnTheme = 'modern-minimal' | 'violet-bloom' | 't3-chat' | 'twitter' | 'mocha-mousse' | 'bubblegum' | '';

export interface ThemeConfig {
  theme: ThemeName;
  tweakcnTheme: TweakcnTheme;
  radius: string;
  mode: 'light' | 'dark';
}

const DEFAULT_CONFIG: ThemeConfig = {
  theme: 'default',
  tweakcnTheme: '',
  radius: '0.5',
  mode: 'light',
};

const STORAGE_KEY = 'converse-theme-config';

// Shared neutral light base
const NEUTRAL_LIGHT: Record<string, string> = {
  '--background': '0 0% 100%',
  '--foreground': '222.2 84% 4.9%',
  '--card': '0 0% 100%',
  '--card-foreground': '222.2 84% 4.9%',
  '--popover': '0 0% 100%',
  '--popover-foreground': '222.2 84% 4.9%',
  '--secondary': '210 40% 96.1%',
  '--secondary-foreground': '222.2 47.4% 11.2%',
  '--muted': '210 40% 96.1%',
  '--muted-foreground': '215.4 16.3% 46.9%',
  '--destructive': '0 84.2% 60.2%',
  '--destructive-foreground': '210 40% 98%',
  '--border': '214.3 31.8% 91.4%',
  '--input': '214.3 31.8% 91.4%',
};

// Shared neutral dark base — zinc/neutral palette matching ShadcnStore dark theme
const NEUTRAL_DARK: Record<string, string> = {
  '--background': '240 10% 3.9%',     // near-black neutral (#09090b) — replaces blue-navy
  '--foreground': '0 0% 98%',         // pure near-white (#fafafa)
  '--card': '240 6% 10%',             // elevated dark (#18181c) — clearly distinct from background
  '--card-foreground': '0 0% 98%',
  '--popover': '240 6% 10%',          // same elevation as card — Sheet panels & dropdowns
  '--popover-foreground': '0 0% 98%',
  '--secondary': '240 3.7% 15.9%',    // near-neutral dark (#27272a)
  '--secondary-foreground': '0 0% 98%',
  '--muted': '240 3.7% 15.9%',        // same as secondary
  '--muted-foreground': '240 5% 64.9%', // medium neutral gray
  '--destructive': '0 62.8% 30.6%',
  '--destructive-foreground': '0 0% 98%',
  '--border': '240 3.7% 15.9%',       // visible neutral border on dark background
  '--input': '240 3.7% 15.9%',        // same as border
};

const BASE_PALETTES: Record<ThemeName, Record<string, string>> = {
  default: {
    ...NEUTRAL_LIGHT,
    '--primary': '222.2 47.4% 11.2%',
    '--primary-foreground': '210 40% 98%',
    '--accent': '210 40% 96.1%',
    '--accent-foreground': '222.2 47.4% 11.2%',
    '--ring': '222.2 84% 4.9%',
  },
  blue: {
    ...NEUTRAL_LIGHT,
    '--primary': '221.2 83.2% 53.3%',
    '--primary-foreground': '210 40% 98%',
    '--accent': '214 100% 95%',
    '--accent-foreground': '221.2 60% 30%',
    '--ring': '221.2 83.2% 53.3%',
  },
  green: {
    ...NEUTRAL_LIGHT,
    '--primary': '142.1 76.2% 36.3%',
    '--primary-foreground': '355.7 100% 97.3%',
    '--accent': '142 76% 96%',
    '--accent-foreground': '142.1 60% 25%',
    '--ring': '142.1 76.2% 36.3%',
  },
  red: {
    ...NEUTRAL_LIGHT,
    '--primary': '0 84.2% 60.2%',
    '--primary-foreground': '0 0% 98%',
    '--accent': '0 100% 97%',
    '--accent-foreground': '0 60% 40%',
    '--ring': '0 84.2% 60.2%',
  },
  rose: {
    ...NEUTRAL_LIGHT,
    '--primary': '346.8 77.2% 49.8%',
    '--primary-foreground': '355.7 100% 97.3%',
    '--accent': '347 100% 97%',
    '--accent-foreground': '346.8 60% 35%',
    '--ring': '346.8 77.2% 49.8%',
  },
  orange: {
    ...NEUTRAL_LIGHT,
    '--primary': '24.6 95% 53.1%',
    '--primary-foreground': '60 9.1% 97.8%',
    '--accent': '24 100% 96%',
    '--accent-foreground': '24.6 70% 30%',
    '--ring': '24.6 95% 53.1%',
  },
};

const DARK_PALETTES: Record<ThemeName, Record<string, string>> = {
  default: {
    ...NEUTRAL_DARK,
    '--primary': '0 0% 98%',           // pure near-white — zinc dark standard
    '--primary-foreground': '240 5.9% 10%', // dark neutral for text on white button
    '--accent': '240 3.7% 15.9%',      // neutral dark hover — matches muted
    '--accent-foreground': '0 0% 98%',
    '--ring': '240 4.9% 83.9%',        // neutral light ring for focus states
  },
  blue: {
    ...NEUTRAL_DARK,
    '--primary': '217.2 91.2% 59.8%',
    '--primary-foreground': '222.2 47.4% 11.2%',
    '--accent': '217.2 32.6% 20%',
    '--accent-foreground': '217.2 91.2% 80%',
    '--ring': '217.2 91.2% 59.8%',
  },
  green: {
    ...NEUTRAL_DARK,
    '--primary': '142.1 70.6% 45.3%',
    '--primary-foreground': '144 30% 10%',
    '--accent': '142 40% 15%',
    '--accent-foreground': '142.1 70.6% 70%',
    '--ring': '142.1 70.6% 45.3%',
  },
  red: {
    ...NEUTRAL_DARK,
    '--primary': '0 84.2% 60.2%',
    '--primary-foreground': '0 0% 9%',
    '--accent': '0 40% 15%',
    '--accent-foreground': '0 84.2% 75%',
    '--ring': '0 84.2% 60.2%',
  },
  rose: {
    ...NEUTRAL_DARK,
    '--primary': '346.8 77.2% 60%',
    '--primary-foreground': '346 30% 10%',
    '--accent': '346 40% 15%',
    '--accent-foreground': '346.8 77.2% 75%',
    '--ring': '346.8 77.2% 60%',
  },
  orange: {
    ...NEUTRAL_DARK,
    '--primary': '24.6 95% 53.1%',
    '--primary-foreground': '24 30% 10%',
    '--accent': '24 50% 15%',
    '--accent-foreground': '24.6 95% 70%',
    '--ring': '24.6 95% 53.1%',
  },
};

// Tweakcn named theme overrides — applied on top of the active base palette
const TWEAKCN_ACCENTS: Record<string, Record<string, string>> = {
  'modern-minimal': {
    '--primary': '221.2 83.2% 53.3%',
    '--primary-foreground': '210 40% 98%',
    '--ring': '221.2 83.2% 53.3%',
  },
  'violet-bloom': {
    '--primary': '271.5 81.3% 55.9%',
    '--primary-foreground': '210 40% 98%',
    '--ring': '271.5 81.3% 55.9%',
    '--accent': '270 100% 97%',
    '--accent-foreground': '271 50% 35%',
  },
  't3-chat': {
    '--primary': '346.8 77.2% 29.8%',
    '--primary-foreground': '355.7 100% 97.3%',
    '--ring': '346.8 77.2% 29.8%',
  },
  'twitter': {
    '--primary': '204 93% 54%',
    '--primary-foreground': '0 0% 100%',
    '--ring': '204 93% 54%',
  },
  'mocha-mousse': {
    '--primary': '20 30% 50%',
    '--primary-foreground': '0 0% 98%',
    '--ring': '20 30% 50%',
    '--accent': '30 40% 96%',
    '--accent-foreground': '20 30% 30%',
  },
  'bubblegum': {
    '--primary': '330 81% 60%',
    '--primary-foreground': '0 0% 100%',
    '--ring': '330 81% 60%',
    '--accent': '330 100% 97%',
    '--accent-foreground': '330 60% 40%',
  },
};

function applyTheme(config: ThemeConfig): void {
  const el = document.documentElement;

  // 1. Apply base palette (light or dark) — fall back to 'default' if stored theme is unknown
  const palettes = config.mode === 'dark' ? DARK_PALETTES : BASE_PALETTES;
  const palette = palettes[config.theme] ?? palettes['default'];
  Object.entries(palette).forEach(([prop, value]) => el.style.setProperty(prop, value));

  // 2. Apply tweakcn accent overrides on top
  if (config.tweakcnTheme && TWEAKCN_ACCENTS[config.tweakcnTheme]) {
    const accents = TWEAKCN_ACCENTS[config.tweakcnTheme];
    Object.entries(accents).forEach(([prop, value]) => el.style.setProperty(prop, value));
  }

  // 3. Radius
  el.style.setProperty('--radius', `${config.radius}rem`);

  // 4. Sidebar tokens
  if (config.mode === 'dark') {
    el.style.setProperty('--sidebar-background', '240 5.9% 10%');
    el.style.setProperty('--sidebar-foreground', '240 4.8% 95.9%');
    el.style.setProperty('--sidebar-accent', '240 3.7% 15.9%');
    el.style.setProperty('--sidebar-accent-foreground', '240 4.8% 95.9%');
    el.style.setProperty('--sidebar-border', '240 3.7% 15.9%');
    el.style.setProperty('--sidebar-ring', '240 4.9% 83.9%');
  } else {
    el.style.setProperty('--sidebar-background', '240 5.9% 95%');
    el.style.setProperty('--sidebar-foreground', '240 5.3% 26.1%');
    el.style.setProperty('--sidebar-accent', '240 4.8% 95.9%');
    el.style.setProperty('--sidebar-accent-foreground', '240 5.9% 10%');
    el.style.setProperty('--sidebar-border', '220 13% 91%');
    el.style.setProperty('--sidebar-ring', '240 5.9% 10%');
  }

  // 5. Dark class
  el.classList.toggle('dark', config.mode === 'dark');
}

export function useThemeConfig() {
  const [themeConfig, setThemeConfigState] = useState<ThemeConfig>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<ThemeConfig>;
        // Drop stale theme/tweakcnTheme values from previous versions
        const validThemes: ThemeName[] = ['default', 'blue', 'green', 'red', 'rose', 'orange'];
        const validTweakcn: TweakcnTheme[] = ['modern-minimal', 'violet-bloom', 't3-chat', 'twitter', 'mocha-mousse', 'bubblegum', ''];
        if (parsed.theme && !validThemes.includes(parsed.theme)) parsed.theme = DEFAULT_CONFIG.theme;
        if (parsed.tweakcnTheme !== undefined && !validTweakcn.includes(parsed.tweakcnTheme)) parsed.tweakcnTheme = DEFAULT_CONFIG.tweakcnTheme;
        return { ...DEFAULT_CONFIG, ...parsed };
      }
    } catch { /* ignore */ }
    return DEFAULT_CONFIG;
  });

  useEffect(() => {
    applyTheme(themeConfig);
  }, [themeConfig]);

  const setThemeConfig = useCallback((update: Partial<ThemeConfig> | ((prev: ThemeConfig) => ThemeConfig)) => {
    setThemeConfigState((prev) => {
      const next = typeof update === 'function' ? update(prev) : { ...prev, ...update };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const resetTheme = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setThemeConfigState(DEFAULT_CONFIG);
  }, []);

  return { themeConfig, setThemeConfig, resetTheme };
}

export { BASE_PALETTES, DARK_PALETTES, TWEAKCN_ACCENTS };
